import { useCallback, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import axiosInstance from "config/axiosInstance";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import WaOutlineButton, { WA_OUTLINE_BTN_SX } from "./WaOutlineButton";
import VuiTypography from "components/VuiTypography";
import { formatTimePeru, toPeruISOString } from "utils/datePeru";
import useWhatsAppEvents from "../hooks/useWhatsAppEvents";
import { formatEventMessage, getEventMeta } from "utils/whatsappUi";

export default function EventsPanel({ onEvent, liveDefault = true }) {
  const [events, setEvents] = useState([]);
  const [live, setLive] = useState(liveDefault);

  const pushEvent = useCallback(
    (event) => {
      const entry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: event.type,
        message: formatEventMessage(event.type, event.payload),
        meta: getEventMeta(event.type),
        timestamp: event.raw?.timestamp || toPeruISOString(new Date()),
      };
      setEvents((prev) => [entry, ...prev].slice(0, 50));
      onEvent?.(event);
    },
    [onEvent]
  );

  useWhatsAppEvents({ enabled: live, onEvent: pushEvent });

  const loadRecent = async () => {
    try {
      const res = await axiosInstance.get("/whatsapp/events/recent", { params: { limit: 30 } });
      if (res.data.success) {
        const mapped = (res.data.events || []).map((ev) => ({
          id: ev.id || `${ev.timestamp}-${ev.type}`,
          type: ev.type,
          message: formatEventMessage(ev.type, ev.payload),
          meta: getEventMeta(ev.type),
          timestamp: ev.timestamp,
        }));
        setEvents(mapped);
      }
    } catch {
      /* silencioso */
    }
  };

  useEffect(() => {
    loadRecent();
  }, []);

  return (
    <VuiBox>
      <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <VuiBox>
          <VuiTypography variant="h5" color="white" fontWeight="bold">
            Actividad en vivo
          </VuiTypography>
          <VuiTypography variant="button" color="text">
            Conexión, envíos y alertas
          </VuiTypography>
        </VuiBox>
        <VuiBox display="flex" gap={1} flexWrap="wrap">
          <WaOutlineButton size="small" onClick={loadRecent}>
            Actualizar
          </WaOutlineButton>
          <VuiButton
            color={live ? "success" : "info"}
            variant={live ? "gradient" : "outlined"}
            size="small"
            sx={live ? undefined : WA_OUTLINE_BTN_SX}
            onClick={() => setLive((v) => !v)}
          >
            {live ? "En vivo" : "Pausado"}
          </VuiButton>
        </VuiBox>
      </VuiBox>

      <Card
        sx={{
          p: 0,
          maxHeight: 420,
          overflow: "auto",
          background: "linear-gradient(127deg, rgba(6,11,38,0.96) 28%, rgba(26,35,126,0.25) 100%)",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        {events.length === 0 ? (
          <VuiBox py={4} textAlign="center">
            <VuiTypography variant="button" color="text">
              Sin actividad reciente
            </VuiTypography>
          </VuiBox>
        ) : (
          events.map((event) => (
            <VuiBox
              key={event.id}
              px={2}
              py={1.5}
              sx={{
                borderBottom: "1px solid rgba(255,255,255,0.06)",
                "&:last-child": { borderBottom: "none" },
              }}
            >
              <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <VuiBox display="flex" alignItems="flex-start" gap={1} flex={1}>
                  <VuiTypography variant="button" color="white" sx={{ lineHeight: 1.6 }}>
                    {event.meta.icon}
                  </VuiTypography>
                  <VuiBox>
                    <Chip
                      label={event.meta.title}
                      color={event.meta.color}
                      size="small"
                      sx={{ mb: 0.5, fontWeight: 600 }}
                    />
                    <VuiTypography variant="caption" color="text" display="block">
                      {event.message}
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
                <VuiTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap" }}>
                  {formatTimePeru(event.timestamp)}
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          ))
        )}
      </Card>
    </VuiBox>
  );
}
